import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { VFC } from 'react';
import { useEffect, useState } from 'react';
import TextField from '@components/forms/fields/TextField';
import Link from '@components/routing/Link';
import { useCheckReferralCodeLazyQuery } from '@graphql/client/generated/checkReferralCode.generated';
import { useRegisterMutation } from '@graphql/client/generated/register.generated';
import isValidationError from '@graphql/errors/validation/isValidationError';
import { zodResolver } from '@hookform/resolvers/zod';
import AccountType from '@lib/types/enums/AccountType';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import flatten from '@utils/flatten';

export function useRegisterFormSchema() {
  const { t } = useTranslation();

  return z
    .object({
      code: z.string().nonempty(),
      username: z.string().min(4).nonempty(),
      email: z
        .string()
        .email(t('forms.email.error.invalid', { defaultValue: 'You must enter a valid email address to continue.' })),
      password: z.string().min(8).nonempty(),
      passwordConfirmation: z.string().min(8).nonempty(),
      accountType: z.nativeEnum(AccountType),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t('forms.password.error.mismatch', { defaultValue: 'Passwords must match' }),
      path: ['passwordConfirmation'],
    });
}

type RegisterFormData = z.infer<ReturnType<typeof useRegisterFormSchema>>;

export interface RegisterFormProps {
  /** Actions to perform on form submission. */
  onRegister?: () => void;
}

/**
 * Form allowing new user to create a DeepSquare account.
 */
const RegisterForm: VFC<RegisterFormProps> = ({ onRegister }) => {
  const router = useRouter();
  const schema = useRegisterFormSchema();
  const { control, handleSubmit, setValue, setError, clearErrors } = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
    defaultValues: {
      code: flatten(router.query.r),
      accountType: AccountType.PRIVATE,
    },
  });
  const { t } = useTranslation();

  useEffect(() => {
    const newCode = flatten(router.query.r);

    if (newCode) {
      setValue('code', newCode);
    }
  }, [router.query.r, setValue]);

  const code: string | undefined = useWatch({ name: 'code', control });
  const [validCode, setValidCode] = useState(false);

  const [checkCode, { loading: checking }] = useCheckReferralCodeLazyQuery({
    onCompleted: (d) => {
      if (!d) return;
      setValidCode(d.checkReferralCode);
      clearErrors();
    },
    onError: ({ graphQLErrors }) => {
      const error = graphQLErrors?.[0];
      if (!isValidationError(error)) return;

      for (const [key, errors] of Object.entries(error.extensions.errors)) {
        setError(key as any, {
          message: errors[0],
        });
      }
    },
  });

  const [registerGQL, { loading }] = useRegisterMutation({
    onCompleted: onRegister,
    onError: ({ graphQLErrors }) => {
      const error = graphQLErrors?.[0];
      if (!isValidationError(error)) return;

      for (const [key, errors] of Object.entries(error.extensions.errors)) {
        setError(key as any, {
          message: errors[0],
        });

        // Remove the passwords
        setValue('password', '');
        setValue('passwordConfirmation', '');
      }
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    await registerGQL({ variables: { ...data } });
  };

  useEffect(() => {
    if (typeof code !== 'string' || code.length < 8) {
      return;
    }

    void checkCode({ variables: { code } });
  }, [checkCode, code]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test="RegisterForm">
      <div className="mb-2">
        {!validCode ? (
          <div className="flex flex-col items-center space-y-4">
            <h1 className="text-2xl font-semibold">
              <Trans i18nKey="auth:welcome-to-deepsquare">Welcome to DeepSquare!</Trans>
            </h1>
            <div className="text-center">
              <Trans i18nKey="auth:register-intro">
                To sign up you need a referral code from a DeepSquare community member. <br />
                If you don’t have one please
                <Link href="https://share-eu1.hsforms.com/1AKo5bsMDSLW_KYbAmTXriAev6gi">click here</Link>.
              </Trans>
            </div>
          </div>
        ) : (
          <Trans i18nKey="auth:welcome-one-step-closer">
            You are one step closer to becoming a member of the DeepSquare community.
          </Trans>
        )}
      </div>

      <div className="relative">
        <TextField
          control={control}
          name="code"
          label={<Trans i18nKey="forms.referral-code.label">Referral code</Trans>}
          placeholder="38vY33z2h95"
          InputLabelProps={router.query.r ? { shrink: true } : {}}
        />

        {checking && (
          <div className="absolute right-8 top-9 w-4 h-4 rounded-full border-b-2 animate-spin border-disabledText" />
        )}
      </div>

      {validCode && (
        <>
          <hr className="my-4" />
          <div>
            <Tooltip
              title={
                <Trans i18nKey="forms.username.tooltip">This username is how you’ll be known by the community</Trans>
              }
            >
              <TextField
                control={control}
                name="username"
                label={<Trans i18nKey="forms.username.label">Username</Trans>}
              />
            </Tooltip>
          </div>

          <TextField
            control={control}
            name="email"
            type="email"
            label={<Trans i18nKey="forms.email-address.label">Email address</Trans>}
          />
          <TextField
            control={control}
            name="password"
            type="password"
            label={<Trans i18nKey="forms.password.label">Password</Trans>}
          />
          <TextField
            control={control}
            name="passwordConfirmation"
            type="password"
            label={<Trans i18nKey="forms.confirm-password.label">Confirm password</Trans>}
          />
          <TextField
            control={control}
            name="accountType"
            select
            SelectProps={{ native: true }}
            label={<Trans i18nKey="forms.account-type.label">Account type</Trans>}
          >
            <option value={AccountType.PRIVATE}>
              {t('forms.account-type.values.private', { defaultValue: 'Private account' })}
            </option>
            <option value={AccountType.COMPANY}>
              {t('forms.account-type.values.company', { defaultValue: 'On behalf of a company' })}
            </option>
          </TextField>

          <LoadingButton type="submit" fullWidth loading={loading} data-test="RegisterForm--button">
            <Trans i18nKey="auth:register">Register</Trans>
          </LoadingButton>
        </>
      )}
    </form>
  );
};

export default RegisterForm;
